/**
 * QuizResults Component
 *
 * Results screen displayed after completing a quiz.
 * Shows score, time, wrong count, best score, per-question review, and action buttons.
 *
 * Design: large centered score, three stat pills, collapsible review list.
 * "Try Again" re-fetches quiz for new random selection.
 * "Back to Content" navigates to /operator/content.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Target,
  Clock,
  XCircle,
  Trophy,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  ArrowLeft,
  RotateCcw,
  MessageCircle,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { formatTimer } from './PlayerShell';
import type { QuizResultItem } from '@guild/shared';

interface QuizResultsProps {
  /** Number of correct answers */
  correctAnswers: number;
  /** Total number of questions */
  totalQuestions: number;
  /** Score percentage (0-100) */
  score: number;
  /** Elapsed time in seconds */
  elapsedSeconds: number;
  /** Best score from practice stats (null if first attempt) */
  bestScore: number | null;
  /** Per-question results with correct answers and explanations */
  results: QuizResultItem[];
  /** Original question texts keyed by questionId (for the review list) */
  questionTexts: Map<string, string>;
  /** Called when "Try Again" is tapped */
  onTryAgain: () => void;
  /** SOP + section title for context */
  title: string;
  /** Section ID for coach navigation context */
  sectionId?: string;
}

export function QuizResults({
  correctAnswers,
  totalQuestions,
  score,
  elapsedSeconds,
  bestScore,
  results,
  questionTexts,
  onTryAgain,
  title,
  sectionId,
}: QuizResultsProps) {
  const navigate = useNavigate();
  const [showReview, setShowReview] = useState(false);

  const wrongCount = totalQuestions - correctAnswers;
  const isNewBest = score > (bestScore ?? -1);
  const isPerfect = score === 100;

  return (
    <div className="min-h-screen bg-op-surface">
      <div className="mx-auto max-w-lg px-4 py-8">
        {/* Score display */}
        <div
          className="flex flex-col items-center text-center"
          style={{ animation: 'fadeInUp 0.4s ease-out' }}
        >
          {/* Icon */}
          <div className={cn(
            'flex h-20 w-20 items-center justify-center rounded-full',
            isPerfect ? 'bg-guild-mint/50' : score >= 60 ? 'bg-emerald-100' : 'bg-amber-100',
          )}>
            <Target
              size={36}
              strokeWidth={1.5}
              className={cn(
                isPerfect ? 'text-guild-green' : score >= 60 ? 'text-emerald-600' : 'text-amber-600',
              )}
            />
          </div>

          {/* Score fraction */}
          <p className="mt-5 text-4xl font-bold text-op-text-primary">
            {correctAnswers} / {totalQuestions}
          </p>

          {/* Score percentage */}
          <p className={cn(
            'mt-1 text-lg font-semibold',
            isPerfect ? 'text-guild-green' : score >= 60 ? 'text-emerald-600' : 'text-amber-600',
          )}>
            {score}% Correct
          </p>

          {/* New best badge */}
          {isNewBest && (
            <div
              className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-guild-mint/50 px-3 py-1"
              style={{ animation: 'fadeInUp 0.4s ease-out 0.2s both' }}
            >
              <Trophy size={14} strokeWidth={1.5} className="text-guild-green" />
              <span className="text-xs font-semibold text-guild-green">New Best Score!</span>
            </div>
          )}

          {/* Perfect score message */}
          {isPerfect && (
            <p className="mt-3 text-sm font-medium text-guild-green">
              Perfect score! Outstanding work.
            </p>
          )}

          {/* Title context */}
          <p className="mt-4 text-sm text-op-text-secondary line-clamp-2">{title}</p>
        </div>

        {/* Stat pills */}
        <div
          className="mt-6 grid grid-cols-3 gap-3"
          style={{ animation: 'fadeInUp 0.4s ease-out 0.1s both' }}
        >
          <StatPill
            icon={Clock}
            value={formatTimer(elapsedSeconds)}
            label="Time"
          />
          <StatPill
            icon={XCircle}
            value={String(wrongCount)}
            label="Wrong"
            variant={wrongCount > 0 ? 'danger' : 'success'}
          />
          <StatPill
            icon={Trophy}
            value={bestScore !== null ? `${Math.max(bestScore, score)}%` : `${score}%`}
            label="Best"
            variant="success"
          />
        </div>

        {/* Action buttons */}
        <div
          className="mt-8 space-y-3"
          style={{ animation: 'fadeInUp 0.4s ease-out 0.2s both' }}
        >
          {/* Review Answers toggle */}
          <button
            type="button"
            onClick={() => setShowReview(!showReview)}
            className="flex w-full min-h-[48px] items-center justify-center gap-2 rounded-xl bg-white px-6 py-3 text-sm font-semibold text-op-text-primary ring-1 ring-gray-200 transition-all duration-150 active:bg-gray-50 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
          >
            {showReview ? (
              <>
                <ChevronUp size={18} strokeWidth={1.5} />
                Hide Answers
              </>
            ) : (
              <>
                <ChevronDown size={18} strokeWidth={1.5} />
                Review Answers
              </>
            )}
          </button>

          {/* Try Again */}
          <button
            type="button"
            onClick={onTryAgain}
            className="flex w-full min-h-[48px] items-center justify-center gap-2 rounded-xl bg-guild-green px-6 py-3 text-sm font-semibold text-white active:scale-[0.98] active:bg-guild-green/90 transition-all duration-150 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
          >
            <RotateCcw size={18} strokeWidth={1.5} />
            Try Again
          </button>

          {/* Review with coach */}
          <button
            type="button"
            onClick={() => navigate(`/operator/coach?mode=quiz_review${sectionId ? `&sectionId=${sectionId}` : ''}`)}
            className="flex w-full min-h-[48px] items-center justify-center gap-2 rounded-xl bg-white px-6 py-3 text-sm font-semibold text-guild-green ring-1 ring-guild-green/20 transition-colors duration-150 active:bg-guild-green/5 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
          >
            <MessageCircle size={18} strokeWidth={1.5} />
            Review with Coach
          </button>

          {/* Back to Content */}
          <button
            type="button"
            onClick={() => navigate('/operator/content')}
            className="flex w-full min-h-[48px] items-center justify-center gap-2 rounded-xl bg-op-muted px-6 py-3 text-sm font-semibold text-op-text-primary active:bg-gray-200 transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
          >
            <ArrowLeft size={18} strokeWidth={1.5} />
            Back to Content
          </button>
        </div>

        {/* Answer review list */}
        {showReview && (
          <div
            className="mt-6 space-y-3"
            style={{ animation: 'fadeInUp 0.3s ease-out' }}
          >
            <p className="text-xs font-semibold uppercase tracking-widest text-op-text-tertiary">
              Answer Review
            </p>
            {results.map((result, index) => (
              <div
                key={result.questionId}
                className={cn(
                  'rounded-2xl bg-white p-4 ring-1',
                  result.isCorrect ? 'ring-emerald-200' : 'ring-red-200',
                )}
              >
                <div className="flex items-start gap-3">
                  {result.isCorrect ? (
                    <CheckCircle2
                      size={18}
                      strokeWidth={1.5}
                      className="mt-0.5 flex-shrink-0 text-emerald-600"
                    />
                  ) : (
                    <XCircle
                      size={18}
                      strokeWidth={1.5}
                      className="mt-0.5 flex-shrink-0 text-red-600"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-op-text-primary">
                      {questionTexts.get(result.questionId) ?? `Question ${index + 1}`}
                    </p>
                    {!result.isCorrect && (
                      <p className="mt-1 text-xs text-red-600">
                        Correct: <span className="font-semibold">{result.correctAnswer}</span>
                      </p>
                    )}
                    {result.explanation && (
                      <p className="mt-1.5 text-xs leading-relaxed text-op-text-tertiary">
                        {result.explanation}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// StatPill (internal)
// ============================================

function StatPill({
  icon: Icon,
  value,
  label,
  variant = 'default',
}: {
  icon: React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;
  value: string;
  label: string;
  variant?: 'default' | 'success' | 'danger';
}) {
  return (
    <div className="flex flex-col items-center rounded-2xl bg-white p-3 ring-1 ring-black/[0.04]">
      <Icon
        size={16}
        strokeWidth={1.5}
        className={cn(
          variant === 'success' && 'text-emerald-600',
          variant === 'danger' && 'text-red-500',
          variant === 'default' && 'text-op-text-tertiary',
        )}
      />
      <p className="mt-1.5 text-lg font-bold text-op-text-primary">{value}</p>
      <p className="text-xs text-op-text-tertiary">{label}</p>
    </div>
  );
}
